import { useEffect, useMemo, useRef, useState } from "react";
import { Chessboard } from "react-chessboard";
import type { ChessboardOptions } from "react-chessboard";
import { applySanMove, applyUciMove, fenSideToMove } from "@onchainreps/chess";
import type { DrillItem } from "../lib/drills";
import { isCorrectMove } from "../lib/drills";

export type DrillStatus = "idle" | "correct" | "wrong";

interface DrillBoardProps {
  drill: DrillItem;
  onAnswer: (correct: boolean, attempts: number) => void;
  onNext: () => void;
}

export function DrillBoard({ drill, onAnswer, onNext }: DrillBoardProps) {
  const [fen, setFen] = useState(drill.fen);
  const [status, setStatus] = useState<DrillStatus>("idle");
  const [attempts, setAttempts] = useState(0);
  const [hints, setHints] = useState(0);
  const [solution, setSolution] = useState(false);
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
  const [trail, setTrail] = useState<string[]>([]);

  useEffect(() => {
    setFen(drill.fen);
    setStatus("idle");
    setAttempts(0);
    setHints(0);
    setSolution(false);
    setLastMove(null);
    setTrail([]);
  }, [drill]);

  const sideToMove = fenSideToMove(fen);
  const expected = drill.expectedUci[0] ?? "";

  const squareStyles = useMemo(() => {
    const styles: Record<string, React.CSSProperties> = {};
    if (hints >= 1 && expected) {
      styles[expected.slice(0, 2)] = {
        backgroundColor: "rgba(255, 214, 102, 0.45)",
      };
    }
    if (hints >= 2 && expected) {
      styles[expected.slice(2, 4)] = { backgroundColor: "rgba(255, 214, 102, 0.58)" };
    }
    if (lastMove) {
      styles[lastMove.from] = { backgroundColor: "rgba(250, 214, 92, 0.42)" };
      styles[lastMove.to] = { backgroundColor: "rgba(250, 214, 92, 0.58)" };
    }
    if (status === "wrong") {
      styles[expected.slice(0, 2)] = { backgroundColor: "rgba(180, 35, 42, 0.35)" };
      styles[expected.slice(2, 4)] = { backgroundColor: "rgba(180, 35, 42, 0.45)" };
    }
    return styles;
  }, [expected, hints, lastMove, status]);

  const arrows = useMemo(() => {
    if (!expected) return [];
    const revealed = hints >= 3 || status === "correct" || solution;
    if (!revealed) return [];
    return [
      {
        startSquare: expected.slice(0, 2),
        endSquare: expected.slice(2, 4),
        color: status === "wrong" || solution ? "rgba(180, 35, 42, 0.7)" : "rgba(29, 78, 216, 0.7)",
      },
    ];
  }, [expected, hints, solution, status]);

  const options: ChessboardOptions = {
    position: fen,
    boardOrientation: sideToMove,
    allowDragging: status === "idle" && !solution,
    showNotation: true,
    animationDurationInMs: 180,
    darkSquareStyle: { backgroundColor: "#3a5a77" },
    lightSquareStyle: { backgroundColor: "#dbe6f0" },
    boardStyle: { borderRadius: "6px", boxShadow: "none" },
    squareStyles,
    arrows,
    onPieceDrop: ({ sourceSquare, targetSquare }) => {
      if (status !== "idle" || solution) return false;
      if (!sourceSquare || !targetSquare) return false;
      const uci = `${sourceSquare}${targetSquare}`;
      const applied = applyUciMove(fen, uci);
      if (!applied.legal) return false;

      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      setFen(applied.fen);
      setLastMove({ from: sourceSquare, to: targetSquare });

      if (isCorrectMove(drill, applied.uci)) {
        setStatus("correct");
        onAnswer(true, nextAttempts);
        return true;
      }

      setStatus("wrong");
      return true;
    },
  };

  const showSolution = () => {
    const applied = applyUciMove(fen, expected);
    if (applied.legal) {
      setFen(applied.fen);
      setLastMove({ from: expected.slice(0, 2), to: expected.slice(2, 4) });
    }
    setSolution(true);
    setStatus("wrong");
    onAnswer(false, Math.max(attempts, 1));
  };

  const playTrail = () => {
    let current = fen;
    const played: string[] = [];
    for (const san of trail) {
      const applied = applySanMove(current, san);
      if (!applied.legal) break;
      current = applied.fen;
      played.push(san);
    }
    if (played.length > 0) setFen(current);
  };

  const trailPlayedRef = useRef(false);

  useEffect(() => {
    trailPlayedRef.current = false;
  }, [drill]);

  useEffect(() => {
    if (status !== "correct" || trail.length === 0 || trailPlayedRef.current) return;
    trailPlayedRef.current = true;
    const timer = window.setTimeout(playTrail, 420);
    return () => window.clearTimeout(timer);
  }, [status, trail]);

  const verdict =
    status === "correct"
      ? "Correct"
      : status === "wrong"
        ? solution
          ? "Here is the move"
          : "Not that one"
        : "";

  return (
    <div className="drill">
      <div className="drill__board">
        <Chessboard options={options} />
      </div>

      <div className="drill__meta">
        <div className="drill__prompt">
          <span className="drill__kicker">{drill.kind === "puzzle" ? "Your move" : "Recall the move"}</span>
          <strong>{drill.title}</strong>
          <span className="drill__sub">{drill.subtitle}</span>
        </div>
        <div className={`drill__verdict drill__verdict--${status}`}>{verdict}</div>
      </div>

      {drill.tags.length > 0 ? (
        <div className="tagrow">
          {drill.tags.slice(0, 4).map((tag) => (
            <span className="tag" key={tag}>
              {tag.replace(/([A-Z])/g, " $1").toLowerCase()}
            </span>
          ))}
        </div>
      ) : null}

      <div className="drill__actions">
        {status === "idle" ? (
          <button
            className="btn btn--ghost"
            onClick={() => setHints((value) => Math.min(3, value + 1))}
            disabled={hints >= 3}
          >
            {hints === 0 ? "Hint" : hints === 1 ? "More" : hints === 2 ? "Show it" : "No more hints"}
          </button>
        ) : null}

        {status === "wrong" && !solution ? (
          <>
            <button className="btn btn--ghost" onClick={() => setStatus("idle")}>
              Try again
            </button>
            <button className="btn btn--primary" onClick={showSolution}>
              Show the move
            </button>
          </>
        ) : null}

        {status === "correct" ? (
          <button className="btn btn--primary" onClick={onNext}>
            Next
          </button>
        ) : null}

        {solution ? (
          <button className="btn btn--primary" onClick={onNext}>
            Continue
          </button>
        ) : null}
      </div>
    </div>
  );
}
